import { useCallback, useEffect, useMemo, useState } from 'react'
import { useBeforeUnload, useParams } from 'react-router-dom'
import { toastSuccess } from '@/utils/toast'
import { StudioRoute, useGetRouteTypes, useUpdateRoute } from '@/lib/react-query'
import * as monacoApi from 'monaco-editor/esm/vs/editor/editor.api'
import { EditorEvent } from '@/config/editor'
import { usePrompt } from '@/hooks/usePrompt'
import { CodeAppConfig } from '@/lib/knots-api'
import { CodeEditorConfig } from '@/types'
import { useButtonNavigateClick } from '@/hooks/useButtonNavigateClick'

export type ModalName =
  | 'snippet'
  | 'new-snippet'
  | 'secret'
  | 'new-secret'
  | 'connector'
  | 'new-connector'
  | 'history'
  | 'zendesk'
  | 'route-settings'

export type UseSaveRouteArgs = {
  projectId: number
  id: number
  name: string
  code: string
  onSuccess?: () => void
}

const DEFAULT_SCROLL = {
  scrollHeight: 0,
  scrollHeightChanged: false,
  scrollLeft: 0,
  scrollLeftChanged: false,
  scrollTop: 0,
  scrollTopChanged: false,
  scrollWidth: 0,
  scrollWidthChanged: false,
}

export const useGoBack = () => {
  return useButtonNavigateClick('/dashboard')
}

export const useSaveRoute = ({ projectId, id, code, name, onSuccess }: UseSaveRouteArgs) => {
  const mutation = useUpdateRoute(projectId, id)

  const save = async () => {
    await mutation.mutateAsync({ route: { name }, code })
    toastSuccess(`Route ${name} saved`, 'Now you can close tab')

    onSuccess?.()
  }

  return [mutation.isPending, save] as const
}

export const useRouteName = (route: StudioRoute | undefined) => {
  const [name, setName] = useState(route?.name ?? '')

  useEffect(() => {
    setName(route?.name ?? '')
  }, [route?.name])
  return [name, setName] as const
}
export const usePageParams = () => {
  const { projectId, routeId } = useParams()

  return {
    projectId: parseInt(projectId!),
    routeId: parseInt(routeId!),
  }
}

export const useModal = () => {
  const [name, setName] = useState<ModalName>()
  const [props, setProps] = useState<any>()

  const open = (name: ModalName, props?: any) => {
    setName(name)
    setProps(props)
  }

  const close = () => setName(undefined)

  // TODO Improve types for props
  return { name, open, close, props }
}

export const useEditor = (initialCode: string) => {
  const [cursorPosition, setCursorPosition] = useState(0)
  const [code, setCode] = useState(initialCode)
  const [scroll, setScroll] = useState<monacoApi.IScrollEvent>(DEFAULT_SCROLL)
  const [selection, setSelection] = useState('')

  return {
    cursorPosition,
    code,
    scroll,
    selection,
    onModelChange: setCode,
    onCursorChanged: useCallback((value: string) => setCursorPosition(parseInt(value)), []),
    onScrollChange: setScroll,
    onSelectionChanged: setSelection,
  }
}

export const usePasteAtCursor = (code: string, cursorPosition: number) => {
  const [parentValue, setParentValue] = useState<string>()

  const paste = (value: string) => {
    const r = code.split('\n')
    r.splice(cursorPosition - 1, 0, value)
    setParentValue(r.join('\n'))
  }

  return [parentValue, paste] as const
}

/**
 * Hook for showing confirm modal each time leaving page with unsaved changes
 * @param dependencies
 * returns Reset function
 */
export const useConfirmUnsaved = (dependencies: Array<string | number>) => {
  const [isChanged, setIsChanged] = useState<boolean | undefined>(undefined)

  const reset = useCallback(() => setIsChanged(false), [setIsChanged])

  const onLocationChange = useCallback(
    () => (isChanged ? !window.confirm('You have unsaved changes, are you sure you want to leave?') : false),
    [isChanged]
  )

  const onBeforeUnload = useCallback(
    (event: any) => {
      if (!isChanged) return
      event.preventDefault()
      event.returnValue = ''
    },
    [isChanged]
  )

  usePrompt(onLocationChange, !!isChanged)
  useBeforeUnload(onBeforeUnload, { capture: true })

  // Skip initial change
  useEffect(() => {
    setIsChanged(isChanged !== undefined)
  }, dependencies)

  return reset
}

/**
 * Hook for support of keyboard events from the editor
 * @param save
 */
export const useEditorShortcuts = (save: () => void) => {
  useEffect(() => {
    const handler = async (msg: MessageEvent) => {
      const m = msg.data
      switch (m.type) {
        case EditorEvent.Save:
          await save()
          break
      }
    }

    window.addEventListener('message', handler)

    return () => {
      window.removeEventListener('message', handler)
    }
  }, [save])
}

export const useEditorConfig = (routeType: StudioRoute['type'], config: CodeAppConfig): CodeEditorConfig => {
  const routeTypes = useGetRouteTypes(routeType)

  return useMemo(
    () => ({
      ...config,
      types: routeTypes.data!.types,
    }),
    [routeType, routeTypes.data]
  )
}
